import "./style/global.less";
import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { createStore } from "redux";
import { Provider } from "react-redux";
import { ConfigProvider } from "@arco-design/web-react";
import zhCN from "@arco-design/web-react/es/locale/zh-CN";
import enUS from "@arco-design/web-react/es/locale/en-US";
import { BrowserRouter, Switch, Route } from "react-router-dom";
import request from "@/utils/request";
import rootReducer, { GlobalState } from "./store";
import PageLayout from "./layout";
import { GlobalContext } from "./context";
import Login from "./pages/login";
import checkLogin from "./utils/checkLogin";
import changeTheme from "./utils/changeTheme";
import useStorage from "./utils/useStorage";
import "./mock";
import { normalizePermissions } from "./utils/permissions";

const RESIZE_OBSERVER_ERROR =
  /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/;

window.addEventListener("error", (event) => {
  if (RESIZE_OBSERVER_ERROR.test(event?.message || "")) {
    event.stopImmediatePropagation();
  }
});

type UserInfoResponse = {
  code: number;
  data?:
    | (Partial<GlobalState["userInfo"]> & {
        authorities?: string[];
        permissions?: GlobalState["userInfo"]["permissions"];
      })
    | null;
  message?: string;
};

type MenuResponse = {
  code: number;
  data?: any[];
  message?: string;
};

const store = createStore(rootReducer);

function Index() {
  const [lang, setLang] = useStorage("arco-lang", "zh-CN");
  const [theme, setTheme] = useStorage("arco-theme", "light");

  function getArcoLocale() {
    switch (lang) {
      case "zh-CN":
        return zhCN;
      case "en-US":
        return enUS;
      default:
        return zhCN;
    }
  }

  const fetchUserInfo = async () => {
    store.dispatch({
      type: "update-userInfo",
      payload: { userLoading: true },
    });
    try {
      const response = await request.get<UserInfoResponse>("/admin/info");
      if (response.code !== 200 || !response.data) {
        throw new Error(response.message || "获取用户信息失败");
      }
      const permissions = normalizePermissions(
        response.data.permissionList || response.data.authorities
      );
      store.dispatch({
        type: "update-userInfo",
        payload: {
          userInfo: { ...response.data, permissions },
          userLoading: false,
        },
      });
    } catch (error) {
      store.dispatch({
        type: "update-userInfo",
        payload: { userLoading: false },
      });
    }
  };

  const fetchMenus = async () => {
    store.dispatch({
      type: "update-menus",
      payload: { menuLoading: true },
    });
    try {
      const response = await request.get<MenuResponse>("/menu/getList");
      if (response.code !== 200 || !response.data) {
        throw new Error(response.message || "获取菜单失败");
      }
      store.dispatch({
        type: "update-menus",
        payload: { menus: response.data, menuLoading: false },
      });
    } catch (error) {
      store.dispatch({
        type: "update-menus",
        payload: { menuLoading: false },
      });
    }
  };

  useEffect(() => {
    if (checkLogin()) {
      fetchUserInfo();
      fetchMenus();
    } else if (window.location.pathname.replace(/\//g, "") !== "login") {
      window.location.pathname = "/login";
    }
  }, []);

  useEffect(() => {
    changeTheme(theme);
  }, [theme]);

  const contextValue = {
    lang,
    setLang,
    theme,
    setTheme,
  };

  return (
    <BrowserRouter>
      <ConfigProvider
        locale={getArcoLocale()}
        componentConfig={{
          Card: {
            bordered: false,
          },
          List: {
            bordered: false,
          },
          Table: {
            border: false,
          },
        }}
      >
        <Provider store={store}>
          <GlobalContext.Provider value={contextValue}>
            <Switch>
              <Route path="/login" component={Login} />
              <Route path="/" component={PageLayout} />
            </Switch>
          </GlobalContext.Provider>
        </Provider>
      </ConfigProvider>
    </BrowserRouter>
  );
}

const container = document.getElementById("root");
if (container) {
  createRoot(container).render(<Index />);
}
